import { MessagingMapDraft, MessagingScript } from '../../models/messaging-map.model';

/**
 * Pure helpers for the Messaging Map route chip on sequence pages. Free of
 * Angular so they are unit-tested here and port unchanged to the Next.js app.
 *
 * A script links a campaign by `providerCampaignId`, the same id a sequence
 * stores, so the match is exact. The link is campaign level only: there is no
 * step-level attribution, so the chip names the script and its lead felt need,
 * never a cell.
 */

export type RouteLaneStatus = 'on_map' | 'not_chosen' | 'not_on_map';

export interface StrategyRoute {
  scriptId: string;
  scriptName: string;
  laneStatus: RouteLaneStatus;
  /** The felt need's label when `laneStatus` is 'on_map', else null. */
  laneLabel: string | null;
}

/** A campaign id as a comparable string; null when missing or blank. */
export function normalizeCampaignId(id: unknown): string | null {
  if (typeof id !== 'string' && typeof id !== 'number') {
    return null;
  }
  const text = String(id).trim();
  return text ? text : null;
}

/**
 * Every script linked to this campaign, sorted by name. The backend refuses a
 * second link to a campaign, but older data can still hold one, so this
 * returns all matches instead of picking one.
 */
export function routesForCampaign(
  scripts: MessagingScript[],
  map: MessagingMapDraft | null,
  providerCampaignId: unknown
): StrategyRoute[] {
  const campaignId = normalizeCampaignId(providerCampaignId);
  if (!campaignId) {
    return [];
  }
  const laneLabels = new Map((map?.lanes ?? []).map(lane => [lane.id, lane.label]));
  return scripts
    .filter(script => normalizeCampaignId(script.providerCampaignId) === campaignId)
    .map(script => {
      if (!script.laneId) {
        return { scriptId: script.scriptId, scriptName: script.name, laneStatus: 'not_chosen' as const, laneLabel: null };
      }
      const label = laneLabels.get(script.laneId);
      return label === undefined
        ? { scriptId: script.scriptId, scriptName: script.name, laneStatus: 'not_on_map' as const, laneLabel: null }
        : { scriptId: script.scriptId, scriptName: script.name, laneStatus: 'on_map' as const, laneLabel: label };
    })
    .sort((a, b) => a.scriptName.localeCompare(b.scriptName) || a.scriptId.localeCompare(b.scriptId));
}

/** The felt-need half of the chip text. */
export function routeLaneText(route: StrategyRoute): string {
  switch (route.laneStatus) {
    case 'on_map':
      return `leads with ${route.laneLabel}`;
    case 'not_chosen':
      return 'no felt need chosen';
    case 'not_on_map':
      return 'felt need no longer on the map';
  }
}

/** Accessible name for a chip link; starts with the visible text (WCAG 2.5.3). */
export function routeAriaLabel(route: StrategyRoute): string {
  return `Messaging Map: ${route.scriptName}, ${routeLaneText(route)}. Opens the Messaging Map.`;
}
